import { useRef, useState } from "react";
import { Alert, Button, Checkbox, SectionCard, Select, type SelectOption } from "@/deps/ui-library";
import {
  useCompanyProfile,
  useDocumentBranding,
  useRemoveBrandingLogo,
  useSaveBrandingLogo,
  useSaveDocumentBranding,
} from "@/api/queries";
import type {
  BrandingColour,
  BrandingTemplate,
  DocumentBranding,
  Invoice,
  Receipt,
} from "@/api/types";
import { useTaxWords } from "@/app/session";
import { PageHeader } from "@/components/PageHeader";
import { BrandingPreviewContext, DEFAULT_BRANDING, PALETTE } from "@/components/print/branding";
import { ReceiptPrint } from "@/components/print/ReceiptPrint";
import { TaxInvoicePrint } from "@/components/print/TaxInvoicePrint";
import { calculateTotals } from "@/lib/invoiceTotals";

const TEMPLATES: Array<{ value: BrandingTemplate; label: string }> = [
  { value: "classic", label: "Classic: logo left, details right" },
  { value: "centred", label: "Centred: logo centred, rule under" },
  { value: "compact", label: "Compact: dense, small logo" },
];

const COLOURS = (Object.keys(PALETTE) as BrandingColour[]).map((value) => ({
  value,
  label: PALETTE[value].label,
}));

const SAME_AS_INVOICE = "same";

const MAX_LOGO_BYTES = 2 * 1024 * 1024;

const chosen = (details: { value: SelectOption | SelectOption[] | null }) => {
  const option = Array.isArray(details.value) ? details.value[0] : details.value;
  return option ? String(option.value) : "";
};

/** A made-up invoice and receipt, to preview with. Nothing here is saved or posted. */
function sampleInvoice(): Invoice {
  const lines = [
    {
      n: 1,
      itemName: "Consulting engagement",
      quantity: "2",
      unitPrice: "12000.00",
      vatCategory: "S" as const,
      vatRatePct: "13",
      lineNet: "22000.00",
      allowances: "2000.00",
      accountId: "",
    },
  ];
  const totals = calculateTotals(
    [{ category: "S", rate: "13", grossPrice: "12000.00", quantity: "2", allowances: "2000.00" }],
    [],
  );
  return {
    id: "sample",
    number: "INV-0001",
    customerId: "sample",
    customerName: "Sample Customer Pvt Ltd",
    customerTaxId: "301234567",
    date: new Date().toISOString().slice(0, 10),
    due: new Date().toISOString().slice(0, 10),
    status: "approved",
    payment: "unpaid",
    total: totals.totalWithVat,
    balance: totals.totalWithVat,
    locked: true,
    currency: "NPR",
    lines,
    totals: {
      taxExclusive: totals.totalWithoutVat,
      allowances: "0.00",
      charges: "0.00",
      vat: totals.totalVatAmount,
      taxInclusive: totals.totalWithVat,
      paid: "0.00",
      due: totals.totalWithVat,
    },
    paymentDetails: "Bank transfer: Your bank, A/C 0000000000",
    footerText: undefined,
    history: [],
    payments: [],
  } as unknown as Invoice;
}

function sampleReceipt(): Receipt {
  return {
    id: "sample",
    number: "RCPT-0001",
    customerId: "sample",
    customerName: "Sample Customer Pvt Ltd",
    date: new Date().toISOString().slice(0, 10),
    method: "Bank transfer",
    amount: "24860.00",
    advance: "0.00",
    bankAccountId: "sample",
    bankAccountName: "Your bank",
    applications: [
      { invoiceId: "sample", invoiceNumber: "INV-0001", applied: "24860.00", tdsWithheld: "0.00" },
    ],
  } as unknown as Receipt;
}

/** How printed documents look. The preview uses the same sheets a real document prints with. */
export function DocumentBrandingPage() {
  const { data } = useDocumentBranding();
  return (
    <div className="flex flex-col gap-4">
      <PageHeader title="Document branding" />
      {data ? <BrandingForm saved={data} /> : null}
    </div>
  );
}

function BrandingForm({ saved }: { saved: DocumentBranding }) {
  const say = useTaxWords();
  const { data: seller } = useCompanyProfile();
  const save = useSaveDocumentBranding();
  const saveLogo = useSaveBrandingLogo();
  const removeLogo = useRemoveBrandingLogo();
  const [form, setForm] = useState(saved);
  const [scope, setScope] = useState<"invoice" | "receipt">("invoice");
  const [logoProblem, setLogoProblem] = useState<string | null>(null);
  const picker = useRef<HTMLInputElement>(null);

  // The logo saves as soon as it's chosen, so it comes from the server and not from this form.
  const preview = { ...form, logoData: saved.logoData };
  const { logoData: _saved, ...savedRest } = saved;
  const { logoData: _form, ...formRest } = form;
  const changed = JSON.stringify(formRest) !== JSON.stringify(savedRest);

  const toggle = (key: keyof DocumentBranding, label: string, note: string) => (
    <div key={key} className="flex flex-col gap-1">
      <Checkbox
        label={label}
        checked={form[key] === true}
        onCheckedChange={(details) => setForm({ ...form, [key]: details.checked === true })}
      />
      <p className="text-fg-muted text-xs">{note}</p>
    </div>
  );

  const chooseLogo = (file: File | undefined) => {
    setLogoProblem(null);
    if (!file) return;
    if (file.size > MAX_LOGO_BYTES) {
      setLogoProblem("A logo can be 2 MB at most.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = String(reader.result).split(",")[1] ?? "";
      saveLogo.mutate(base64);
    };
    reader.readAsDataURL(file);
  };

  const problem =
    logoProblem ??
    ((save.error ?? saveLogo.error ?? removeLogo.error) as Error | null)?.message ??
    null;

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(320px,420px)_1fr] lg:items-start">
      <div className="flex flex-col gap-4">
        {problem ? (
          <Alert config={{ variant: "subtle", colorScheme: "danger" }} description={problem} />
        ) : null}

        <SectionCard title="Template and colour">
          <div className="flex flex-col gap-3">
            <Select
              label="Invoices and credit notes"
              options={TEMPLATES}
              value={form.invoiceTemplate}
              showClearButton={false}
              onChange={(details) => {
                const value = chosen(details);
                if (value) setForm({ ...form, invoiceTemplate: value as BrandingTemplate });
              }}
            />
            <Select
              label="Receipts"
              options={TEMPLATES}
              value={form.receiptTemplate}
              showClearButton={false}
              onChange={(details) => {
                const value = chosen(details);
                if (value) setForm({ ...form, receiptTemplate: value as BrandingTemplate });
              }}
            />
            <Select
              label="Colour"
              options={COLOURS}
              value={form.colour}
              showClearButton={false}
              helperText="Used for the document title and the rule under it. Body text stays black."
              onChange={(details) => {
                const value = chosen(details);
                if (value) setForm({ ...form, colour: value as BrandingColour });
              }}
            />
            <Select
              label="Receipt colour"
              options={[{ value: SAME_AS_INVOICE, label: "Same as invoices" }, ...COLOURS]}
              value={form.receiptColour ?? SAME_AS_INVOICE}
              showClearButton={false}
              onChange={(details) => {
                const value = chosen(details);
                if (value) {
                  setForm({
                    ...form,
                    receiptColour: value === SAME_AS_INVOICE ? null : (value as BrandingColour),
                  });
                }
              }}
            />
          </div>
        </SectionCard>

        <SectionCard title="Logo">
          <div className="flex flex-col gap-3">
            {saved.logoData ? (
              <img src={saved.logoData} alt="Company logo" className="max-h-16 max-w-48" />
            ) : (
              <p className="text-fg-muted text-sm">No logo yet.</p>
            )}
            <input
              ref={picker}
              type="file"
              accept="image/png,image/svg+xml"
              aria-label="Logo file"
              className="hidden"
              onChange={(event) => {
                chooseLogo(event.target.files?.[0]);
                event.target.value = "";
              }}
            />
            <span className="flex gap-2">
              <Button
                colorScheme="default"
                variant="outline"
                size="sm"
                disabled={saveLogo.isPending}
                onClick={() => picker.current?.click()}
              >
                {saved.logoData ? "Replace logo" : "Add logo"}
              </Button>
              {saved.logoData ? (
                <Button
                  colorScheme="default"
                  variant="ghost"
                  size="sm"
                  disabled={removeLogo.isPending}
                  onClick={() => removeLogo.mutate()}
                >
                  Remove logo
                </Button>
              ) : null}
            </span>
            <p className="text-fg-muted text-xs">PNG or SVG, 2 MB at most. It saves when chosen.</p>
          </div>
        </SectionCard>

        <SectionCard title="Show on the document">
          <div className="flex flex-col gap-3">
            {toggle("showLogo", "Logo", "At the top of the document.")}
            {toggle("showTaxNumber", say("PAN"), "Your company's number, under its address.")}
            {toggle("showContact", "Contact line", "Phone and email, from the company profile.")}
            {toggle(
              "showPaymentDetails",
              "Bank details",
              "How to pay on an invoice, and the account the money reached on a receipt.",
            )}
            {toggle("showAmountInWords", "Amount in words", "Printed under the totals.")}
            {toggle("showSignature", "Signature strip", "Prepared or received by, and authorised.")}
            {toggle("showDiscount", "Discount column", "On invoices with a discount on a line.")}
          </div>
        </SectionCard>

        <span className="flex gap-2">
          <Button
            colorScheme="primary"
            variant="solid"
            size="sm"
            disabled={!changed || save.isPending}
            onClick={() => save.mutate(form)}
          >
            {save.isPending ? "Saving" : "Save branding"}
          </Button>
          <Button
            colorScheme="default"
            variant="ghost"
            size="sm"
            onClick={() => setForm({ ...DEFAULT_BRANDING, logoData: saved.logoData })}
          >
            Reset
          </Button>
        </span>
      </div>

      <SectionCard
        title="Preview"
        actions={
          <Select
            aria-label="Preview document"
            options={[
              { value: "invoice", label: "Invoice" },
              { value: "receipt", label: "Receipt" },
            ]}
            value={scope}
            showClearButton={false}
            onChange={(details) => {
              const value = chosen(details);
              if (value) setScope(value as "invoice" | "receipt");
            }}
          />
        }
      >
        <BrandingPreviewContext.Provider value={preview}>
          {scope === "invoice" ? (
            <TaxInvoicePrint invoice={sampleInvoice()} seller={seller} />
          ) : (
            <ReceiptPrint receipt={sampleReceipt()} seller={seller} />
          )}
        </BrandingPreviewContext.Provider>
      </SectionCard>
    </div>
  );
}
